import { ArrangementCase, MemorialHubPreview, MissingItem } from "../types";
import { getProduct } from "../data/demo-arrangement-catalog";
import {
  formatDecedentName,
  formatPlannerName,
  selectedKeepsakes,
} from "../utils/format-arrangement";

export function generateArrangementRecommendation(caseDraft: Partial<ArrangementCase>) {
  const disposition = caseDraft.disposition?.type ?? "burial";

  return {
    events:
      disposition === "cremation"
        ? ["Memorial gathering", "Celebration of life", "Reception"]
        : ["Visitation", "Funeral Mass", "Graveside Committal", "Reception"],
    products:
      disposition === "cremation"
        ? ["brushed-bronze-urn", "photo-tribute-program", "guestbook-memory-print"]
        : ["classic-oak-tribute", "monarch-gold-folder", "embossed-folder-bundle"],
    serviceFolderTemplate: "monarch-gold",
    guide:
      "Based on what you selected, a visitation followed by a funeral service and reception may fit your family's needs. Monarch will review this with you before anything is finalized.",
  };
}

export function generateDirectorSummary(arrangementCase: ArrangementCase) {
  const decedentName = formatDecedentName(arrangementCase.decedent);
  const plannerName = formatPlannerName(arrangementCase.planner);
  const casket = arrangementCase.merchandiseSelections
    .map((selection) => getProduct(selection.productId))
    .find((product) => product?.category === "casket");
  const folder = arrangementCase.merchandiseSelections
    .map((selection) => getProduct(selection.productId))
    .find((product) => product?.category === "service_folder");
  const tradition = arrangementCase.servicePlan.faithTradition
    ? `${arrangementCase.servicePlan.faithTradition} `
    : "";

  return `${plannerName} submitted an immediate-need arrangement request for ${decedentName}. The family prefers a traditional ${tradition}service with visitation, funeral Mass, graveside committal, and a small reception. They selected the ${casket?.name ?? "recommended casket"} and ${folder?.name ?? "service folder"}. The digital folder draft is ready, but officiant name, pallbearers, final photo, and cemetery confirmation are missing.`;
}

export function generateObituaryDraft(arrangementCase: ArrangementCase) {
  const decedentName = formatDecedentName(arrangementCase.decedent);
  const biography = arrangementCase.decedent.biographyDraft ?? "";

  return `${decedentName} is remembered with love by family and friends. ${biography} Monarch will help the family review and approve this tribute before it is published.`;
}

export function generateOrderOfService(arrangementCase: ArrangementCase) {
  if (arrangementCase.servicePlan.faithTradition === "Catholic") {
    return [
      "Musical Prelude",
      "Opening Prayer",
      "Scripture Reading",
      "Reflections from Family and Friends",
      "Musical Selection",
      "Eulogy",
      "Closing Prayer",
      "Recessional",
    ];
  }

  if (arrangementCase.servicePlan.style === "celebration_of_life") {
    return [
      "Welcome",
      "Photo Tribute",
      "Family Reflections",
      "Open Sharing",
      "Closing Words",
    ];
  }

  return [
    "Welcome",
    "Opening Reading",
    "Family Reflections",
    "Eulogy",
    "Closing Message",
  ];
}

export function generateMissingItems(arrangementCase: ArrangementCase): MissingItem[] {
  const missing: MissingItem[] = [];

  if (!arrangementCase.servicePlan.events.some((event) => event.notes?.toLowerCase().includes("officiant"))) {
    missing.push({
      id: "missing-officiant",
      label: "Confirm officiant",
      severity: "required",
      section: "Service details",
    });
  }

  if (arrangementCase.digitalFolder.pallbearers.length === 0) {
    missing.push({
      id: "missing-pallbearers",
      label: "Add pallbearers",
      severity: "recommended",
      section: "Digital service folder",
    });
  }

  if (!arrangementCase.decedent.photoUrl) {
    missing.push({
      id: "missing-photo",
      label: "Upload final portrait photo",
      severity: "recommended",
      section: "Memorial hub",
    });
  }

  if (arrangementCase.disposition.type === "burial" && !arrangementCase.disposition.cemeteryName) {
    missing.push({
      id: "missing-cemetery",
      label: "Confirm cemetery location",
      severity: "required",
      section: "Disposition",
    });
  }

  return missing;
}

export function generateFamilyFollowUpMessage(arrangementCase: ArrangementCase) {
  const plannerName = arrangementCase.planner.firstName;

  return `Hi ${plannerName}, thank you for sending these arrangement details to Monarch. We have a strong draft ready. Could you send the officiant name, final portrait photo, pallbearer names, and cemetery confirmation when convenient? A director will review everything with you before anything is finalized.`;
}

export function buildMemorialHubPreview(arrangementCase: ArrangementCase): MemorialHubPreview {
  const keepsakeLabels = selectedKeepsakes(arrangementCase).map((product) => product.name);

  return {
    caseId: arrangementCase.id,
    decedentName: formatDecedentName(arrangementCase.decedent),
    obituaryDraft: generateObituaryDraft(arrangementCase),
    serviceTimeline: arrangementCase.servicePlan.events,
    guestbookEnabled: arrangementCase.guestbook.enabled,
    digitalFolderStatus:
      arrangementCase.digitalFolder.status === "draft_ready" ? "Draft ready" : "Needs review",
    qrValue: `https://wehonor.app/monarch/memorial/${arrangementCase.id}/folder`,
    keepsakeLabels,
    crmSignals: [
      "Guestbook messages become relationship-tagged contacts",
      "Photo uploads create family engagement signals",
      "Private condolences remain family-safe",
      "Keepsake requests are director-reviewed revenue opportunities",
    ],
  };
}
